import Dexie, { type Table } from 'dexie';
import { last, remove, sortBy } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import { beginAudioShutdown, drainAudioWork } from './audioWorkBarrier';
import {
  assertWriteAllowed,
  attachWriteFence,
  bumpEpoch,
  captureEpoch,
  clearWipeIncompleteMarker,
  closeFencedDatabasesForWipe,
  getWipeIncompleteMarker,
  setWipeIncompleteMarker,
  wipeUnfenced,
  withAudioWipeLock,
} from './persistenceV2/writeFence';
import {
  type BackupRecording,
  type Recording,
  type Session,
  type SessionChunk,
} from './types';
import { logger } from './useSessionRecordingStore/lib/logger';
import { getErrorInfo, MINIMUM_STORAGE_BYTES, serializeBlob } from './utils';

const INITIALISE_SESSION_ID = 'initialise';
const INITIALISE_SESSION_RECORDING_ID = 'initialiseRecording';
const INITIALISE_SESSION_RECORDING_CHUNK_ID = 'initialiseRecordingChunk';

class AppDatabase extends Dexie {
  sessions!: Table<Session, string>;
  initialise!: Table<Session, string>;

  constructor() {
    super('HEIDI_AUDIO');

    this.version(3)
      .stores({ sessions: 'id' })
      .upgrade((t) => t.table('sessions').clear());

    this.version(4)
      .stores({ initialise: 'id' })
      .upgrade((t) => t.table('initialise').clear());
  }

  setInitialise(session: Session) {
    assertWriteAllowed(captureEpoch());
    return this.initialise.put(session);
  }

  async createInitialise() {
    const epoch = captureEpoch();
    // Store a representative blob to surface IndexedDB failures before recording.
    const initialiseBlob = await serializeBlob(
      new Blob([new ArrayBuffer(MINIMUM_STORAGE_BYTES)])
    );
    return this.transaction('rw', this.initialise, async () => {
      assertWriteAllowed(epoch);
      const initialise = {
        id: INITIALISE_SESSION_ID,
        recordings: [] as Recording[],
      };
      const initialiseChunk = {
        id: INITIALISE_SESSION_RECORDING_CHUNK_ID,
        sessionId: INITIALISE_SESSION_ID,
        recordingId: INITIALISE_SESSION_RECORDING_ID,
        isProcessed: false,
        wasMissingChunk: false,
        size: 2048,
        duration: 5,
        index: 0,
        blob: initialiseBlob,
      } as SessionChunk;
      initialise.recordings.push({
        id: INITIALISE_SESSION_RECORDING_ID,
        chunks: [initialiseChunk],
        backupRecording: null,
        startDate: new Date(),
        lastChunkIndex: null,
        useTranscriptionServiceV2: false,
      });
      await this.setInitialise(initialise);
    });
  }

  deleteInitialise() {
    const epoch = captureEpoch();
    return this.transaction('rw', this.initialise, async () => {
      assertWriteAllowed(epoch);
      return this.initialise.delete(INITIALISE_SESSION_ID);
    });
  }

  tryToGetSession(sessionId: string) {
    return this.sessions.get(sessionId);
  }

  deleteEmptySessions() {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const sessions = await this.sessions.toArray();
      await Promise.all(
        sessions
          .filter((session) => session.recordings.length === 0)
          .map((session) => this.deleteSession(session.id))
      );
    });
  }

  async getSession(sessionId: string) {
    const session = await this.tryToGetSession(sessionId);
    if (!session) return { id: sessionId, recordings: [] as Recording[] };
    return session;
  }

  deleteSession(sessionId: string) {
    assertWriteAllowed(captureEpoch());
    return this.sessions.delete(sessionId);
  }

  deleteAllSessions() {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const sessions = await this.sessions.toArray();
      await this.sessions.bulkDelete(sessions.map((s) => s.id));
    });
  }

  setSession(session: Session) {
    assertWriteAllowed(captureEpoch());
    return this.sessions.put(session);
  }

  createRecording(
    sessionId: string,
    recordingId: string,
    useTranscriptionServiceV2: boolean,
    userRefId: string
  ) {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(sessionId);

      session.recordings.push({
        id: recordingId,
        userRefId,
        chunks: [],
        backupRecording: null,
        startDate: new Date(),
        lastChunkIndex: null,
        useTranscriptionServiceV2,
      });

      await this.setSession(session);
    });
  }

  addChunk(chunk: SessionChunk) {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(chunk.sessionId);
      const recording = this.lookupRecording(session, chunk.recordingId);
      // A recording is deleted once all its chunks have uploaded, so a trailing
      // chunk write can land after it is gone. The audio is already safe by
      // then, so treat the missing recording as a normal outcome and no-op.
      if (!recording) {
        logger.warn({
          event_type: 'ADD_CHUNK_RECORDING_MISSING',
          recording_id: chunk.recordingId,
        });
        return;
      }
      chunk.blob = await serializeBlob(chunk.blob);
      recording.chunks.push(chunk);
      await this.setSession(this.ensureChunkOrder(session, chunk.recordingId));
    });
  }

  deleteRecording(sessionId: string, recordingId: string) {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(sessionId);
      remove(session.recordings, (r) => r.id === recordingId);
      await this.setSession(session);
    });
  }

  async markChunkAsProcessed(chunk: SessionChunk, deleteBlob?: boolean) {
    const epoch = captureEpoch();
    await this.addChunkIfMissing(chunk);

    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(chunk.sessionId);
      const recording = this.lookupRecording(session, chunk.recordingId);
      if (!recording) {
        logger.warn({
          event_type: 'MARK_CHUNK_PROCESSED_RECORDING_MISSING',
          recording_id: chunk.recordingId,
        });
        return;
      }
      const recordingChunk = this.lookupChunk(recording, chunk.index);
      if (!recordingChunk) {
        logger.warn({
          event_type: 'MARK_CHUNK_PROCESSED_CHUNK_MISSING',
          recording_id: chunk.recordingId,
        });
        return;
      }
      recordingChunk.isProcessed = true;
      if (deleteBlob) delete recordingChunk.blob;
      await this.setSession(this.ensureChunkOrder(session, chunk.recordingId));
    });
  }

  async addChunkIfMissing(chunk: SessionChunk) {
    const epoch = captureEpoch();
    try {
      await this.transaction('rw', this.sessions, async () => {
        assertWriteAllowed(epoch);
        const session = await this.getSession(chunk.sessionId);
        const recording = this.lookupRecording(session, chunk.recordingId);
        // Only markChunkAsProcessed calls this, and it emits the canonical
        // MARK_CHUNK_PROCESSED_RECORDING_MISSING warn for the deleted-recording
        // race, so stay silent here to avoid double-logging one event.
        if (!recording) return;
        const recordingChunk = this.lookupChunk(recording, chunk.index);

        if (!recordingChunk) {
          logger.info({
            event_type: 'PROCESS_CHUNK_RE_ADDED',
            recording_id: chunk.recordingId,
          });
          chunk.wasMissingChunk = true;
          chunk.blob = await serializeBlob(chunk.blob);
          recording.chunks.push(chunk);
          await this.setSession(this.ensureChunkOrder(session, chunk.recordingId));
        }
      });
    } catch (err) {
      logger.error({
        event_type: 'ADD_CHUNK_IF_MISSING_ERROR',
        recording_id: chunk.recordingId,
        details: (await getErrorInfo(err).catch(() => undefined)) ?? {
          errorMessage: String(err),
        },
      });
    }
  }

  lookupRecording(session: Session, recordingId: string) {
    return session.recordings.find((r) => r.id === recordingId);
  }

  lookupChunk(recording: Recording, index: number) {
    return recording.chunks.find((c) => c.index === index);
  }

  ensureChunkOrder(session: Session, recordingId: string) {
    const recording = this.lookupRecording(session, recordingId);

    if (!recording) {
      logger.warn({
        event_type: 'ENSURE_CHUNK_ORDER_RECORDING_MISSING',
        recording_id: recordingId,
      });
      return session;
    }

    recording.chunks = sortBy(recording.chunks, (c) => c.index);
    return session;
  }

  markBackupAsProcessed(sessionId: string, recordingId: string, deleteBlob?: boolean) {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(sessionId);
      const recording = this.lookupRecording(session, recordingId);
      if (!recording) {
        logger.warn({
          event_type: 'MARK_BACKUP_PROCESSED_RECORDING_MISSING',
          recording_id: recordingId,
        });
        return;
      }
      if (!recording.backupRecording) {
        logger.warn({
          event_type: 'MARK_BACKUP_PROCESSED_BACKUP_MISSING',
          recording_id: recordingId,
        });
        return;
      }
      recording.backupRecording.isProcessed = true;
      if (deleteBlob) delete recording.backupRecording.blob;
      await this.setSession(session);
    });
  }

  setRecordingEndDate(sessionId: string) {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(sessionId);
      const recording = last(session.recordings);
      if (!recording) return;
      recording.endDate = new Date();
      await this.setSession(session);
    });
  }

  setRecordingLastChunkIndex(chunk: SessionChunk) {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(chunk.sessionId);
      const recording = this.lookupRecording(session, chunk.recordingId);
      if (!recording) {
        logger.warn({
          event_type: 'SET_LAST_CHUNK_INDEX_RECORDING_MISSING',
          recording_id: chunk.recordingId,
        });
        return;
      }
      recording.lastChunkIndex = chunk.index;
      await this.setSession(session);
    });
  }

  addBackupRecording(backup: BackupRecording) {
    const epoch = captureEpoch();
    return this.transaction('rw', this.sessions, async () => {
      assertWriteAllowed(epoch);
      const session = await this.getSession(backup.sessionId);
      const recording = this.lookupRecording(session, backup.recordingId);
      if (!recording) {
        logger.warn({
          event_type: 'ADD_BACKUP_RECORDING_MISSING',
          recording_id: backup.recordingId,
        });
        return;
      }
      recording.backupRecording = backup;
      await this.setSession(session);
    });
  }

  async getRecording(sessionId: string, recordingId: string) {
    const session = await this.getSession(sessionId);
    return this.lookupRecording(session, recordingId);
  }

  async getCurrentRecording(sessionId: string) {
    const session = await this.getSession(sessionId);
    return last(session.recordings);
  }
}

export const db = new AppDatabase();

/** Lazily loads ledger-only debugger dependencies after the debugger is enabled. */
export const loadLedgerDebugApi = async () => {
  const ledger = await import('./persistenceV2');
  const barrier = await import('./audioWorkBarrier');
  return {
    dbV2: ledger.dbV2,
    getChunkBlob: ledger.getChunkBlob,
    getPipelineMode: ledger.getPipelineMode,
    resolvePipelineMode: ledger.resolvePipelineMode,
    getPendingAudioWorkCount: barrier.getPendingAudioWorkCount,
  };
};
// Attach before first open so every legacy mutation passes through the fence.
attachWriteFence(db, 'legacy');

const LEDGER_DB_NAME = 'HEIDI_AUDIO_LEDGER';
const LEGACY_DB_NAME = 'HEIDI_AUDIO';
const CLEAR_TIMEOUT_MS = 3000;
const COMPLETED_WIPES_KEY = 'heidi-audio-completed-wipes';
const MAX_COMPLETED_WIPES = 16;
const EMPTY_WIPE_LOG_INTERVAL_MS = 60_000;

const errorDetails = (err: unknown) => ({
  error: err instanceof Error ? err.message : String(err),
});

type BlobRowStats = { hasBlob?: boolean; size?: number };

interface ActiveWipe {
  wipeIds: Set<string>;
  reasons: Set<AudioWipeReason>;
  promise: Promise<void>;
}

const readCompletedWipes = (): string[] => {
  try {
    const raw = window.localStorage.getItem(COMPLETED_WIPES_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string')
      : [];
  } catch {
    return [];
  }
};

const hasCompletedWipe = (wipeId: string): boolean =>
  readCompletedWipes().includes(wipeId);

const markWipesCompleted = (wipeIds: Iterable<string>): void => {
  try {
    const completed = [...new Set([...readCompletedWipes(), ...wipeIds])].slice(
      -MAX_COMPLETED_WIPES
    );
    window.localStorage.setItem(COMPLETED_WIPES_KEY, JSON.stringify(completed));
  } catch (err) {
    logger.warn({
      event_type: 'LOGOUT_COMPLETED_WIPE_MARKER_FAILED',
      details: errorDetails(err),
    });
  }
};

const clearCompletedMarker = (): void => {
  const marker = getWipeIncompleteMarker();
  if (marker && hasCompletedWipe(marker)) clearWipeIncompleteMarker(marker);
};

const teardownLocalCapture = async (): Promise<void> => {
  try {
    const { useSessionRecordingStore } =
      await import('./useSessionRecordingStore/useSessionRecordingStore');
    await Promise.resolve(useSessionRecordingStore.getState().teardownForLogout());
  } catch (err) {
    logger.warn({
      event_type: 'LOGOUT_RECORDER_TEARDOWN_FAILED',
      details: errorDetails(err),
    });
  }
};

interface LogoutClearAudioDependencies {
  runWithLock: (work: () => Promise<void>, signal?: AbortSignal) => Promise<void>;
  teardown: () => Promise<void>;
  wipe: typeof wipeUnfenced;
  deleteDatabase: (name: string) => Promise<void>;
}

/** Why the audio wipe ran — a triage dimension on the LOGOUT_* events. */
export type AudioWipeReason = 'user' | 'session-expiry';

export const createLogoutClearAudioController = (
  dependencies: Partial<LogoutClearAudioDependencies> = {}
) => {
  const runWithLock = dependencies.runWithLock ?? withAudioWipeLock;
  const teardown = dependencies.teardown ?? teardownLocalCapture;
  const wipe = dependencies.wipe ?? wipeUnfenced;
  const deleteDatabase =
    dependencies.deleteDatabase ?? ((name: string) => Dexie.delete(name));
  let activeWipe: ActiveWipe | null = null;
  let lastEmptyWipeReportedAt: number | null = null;
  let suppressedEmptyWipes = 0;

  const startWipe = (wipeId: string, reason: AudioWipeReason): ActiveWipe => {
    const wipeIds = new Set([wipeId]);
    const reasons = new Set([reason]);
    // Teardown is per pipeline mode: the ledger latches and discards at once,
    // legacy bounded-awaits the recorder tail. Fence and marker close inside the
    // lock, after teardown settles and strictly before any wipe byte.
    const teardownPromise = teardown();

    const cleared: Record<string, number | boolean> = {};

    const wipeLedger = async () => {
      try {
        const ledger = await wipe(LEDGER_DB_NAME, async (raw) => {
          const [recordings, chunkRows, backupRows] = await Promise.all([
            raw.table('recordings').count(),
            raw.table('chunks').toArray() as Promise<BlobRowStats[]>,
            raw.table('backups').toArray() as Promise<BlobRowStats[]>,
          ]);
          const bytes = (rows: BlobRowStats[]) =>
            rows.reduce((sum, row) => sum + (row.hasBlob ? (row.size ?? 0) : 0), 0);
          return {
            recordings,
            chunks: chunkRows.length,
            backups: backupRows.length,
            audioBytes: bytes(chunkRows) + bytes(backupRows),
          };
        });
        if (ledger.stats) Object.assign(cleared, ledger.stats);
      } catch (err) {
        logger.warn({
          event_type: 'LOGOUT_LEDGER_CLEAR_FALLBACK_DELETE',
          details: errorDetails(err),
        });
        await deleteDatabase(LEDGER_DB_NAME);
        cleared.ledgerDeletedUnopenable = true;
      }
    };

    const wipeLegacy = async () => {
      try {
        const legacy = await wipe(LEGACY_DB_NAME, async (raw) => ({
          legacySessions: await raw.table('sessions').count(),
        }));
        if (legacy.stats) Object.assign(cleared, legacy.stats);
      } catch (err) {
        logger.warn({
          event_type: 'LOGOUT_LEGACY_CLEAR_FALLBACK_DELETE',
          details: errorDetails(err),
        });
        await deleteDatabase(LEGACY_DB_NAME);
        cleared.legacyDeletedUnopenable = true;
      }
    };

    const promise = runWithLock(async () => {
      await teardownPromise;
      bumpEpoch();
      // Marker set inside the lock so a concurrent hydrate cannot clear it
      // while this wipe is pending.
      if (!hasCompletedWipe(wipeId)) setWipeIncompleteMarker(wipeId);
      beginAudioShutdown();

      const drained = await drainAudioWork(CLEAR_TIMEOUT_MS);
      if (!drained) {
        logger.warn({
          event_type: 'LOGOUT_AUDIO_WORK_DRAIN_TIMEOUT',
          details: { reason },
        });
      }

      if ([...wipeIds].every(hasCompletedWipe)) {
        clearCompletedMarker();
        return;
      }

      // Our own open handles must never block the wipe's delete fallback.
      closeFencedDatabasesForWipe();
      const results = await Promise.allSettled([wipeLedger(), wipeLegacy()]);
      const failure = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      );
      if (failure) throw failure.reason;

      markWipesCompleted(wipeIds);
      clearCompletedMarker();
      const reportedReason = reasons.has('user') ? 'user' : 'session-expiry';
      const emptyExpiryWipe =
        reportedReason === 'session-expiry' &&
        Object.values(cleared).every((value) => value === 0);
      const now = Date.now();
      if (
        emptyExpiryWipe &&
        lastEmptyWipeReportedAt !== null &&
        now >= lastEmptyWipeReportedAt &&
        now - lastEmptyWipeReportedAt < EMPTY_WIPE_LOG_INTERVAL_MS
      ) {
        suppressedEmptyWipes += 1;
        return;
      }
      lastEmptyWipeReportedAt = emptyExpiryWipe ? now : null;
      logger.info({
        event_type: 'LOGOUT_CLEAR_AUDIO_DONE',
        details: {
          ...cleared,
          reason: reportedReason,
          ...(suppressedEmptyWipes > 0 ? { suppressedEmptyWipes } : {}),
        },
      });
      suppressedEmptyWipes = 0;
    }).finally(() => {
      if (activeWipe?.promise === promise) activeWipe = null;
    });

    promise.catch(() => undefined);
    return { wipeIds, reasons, promise };
  };

  const logoutClearAudio = async (
    wipeId: string = uuid(),
    reason: AudioWipeReason = 'user'
  ): Promise<void> => {
    if (activeWipe) {
      activeWipe.wipeIds.add(wipeId);
      activeWipe.reasons.add(reason);
      if (!hasCompletedWipe(wipeId)) setWipeIncompleteMarker(wipeId);
    } else {
      activeWipe = startWipe(wipeId, reason);
    }
    const wipe = activeWipe;

    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const outcome = await Promise.race([
        wipe.promise.then(() => 'done' as const),
        new Promise<'timeout'>((resolve) => {
          timer = setTimeout(() => resolve('timeout'), CLEAR_TIMEOUT_MS);
        }),
      ]);
      if (outcome === 'timeout') {
        lastEmptyWipeReportedAt = null;
        logger.error({ event_type: 'LOGOUT_CLEAR_AUDIO_TIMEOUT', details: { reason } });
      }
    } catch (err) {
      lastEmptyWipeReportedAt = null;
      logger.error({
        event_type: 'LOGOUT_CLEAR_AUDIO_FAILED',
        details: { ...errorDetails(err), reason },
      });
    } finally {
      clearTimeout(timer);
    }
  };

  return { logoutClearAudio };
};

let logoutClearAudioController = createLogoutClearAudioController();

export const logoutClearAudio = (
  wipeId?: string,
  reason?: AudioWipeReason
): Promise<void> => logoutClearAudioController.logoutClearAudio(wipeId, reason);

/** Test-only. */
export const __resetLogoutClearAudioForTests = (): void => {
  logoutClearAudioController = createLogoutClearAudioController();
  try {
    window.localStorage.removeItem(COMPLETED_WIPES_KEY);
  } catch {}
};
